import { toast } from '../components/ui/toast';

// Paced FIFO queue in front of sonner for stream-driven notification toasts.
// Bursts of finance events used to call toast() once per event: sonner shows
// only the newest `visibleToasts` while every timer ticks concurrently, so
// most toasts expired unseen. The queue releases toasts one at a time —
// at most MAX_IN_FLIGHT showing, at least MIN_GAP_MS between releases — and
// drains until empty, so no notification skips the toast stream.
//
// User-action toasts (CRUD feedback) intentionally bypass this queue and stay
// instant; only useFinanceStream enqueues here.

export interface NotificationToastOptions {
  description?: string;
  duration?: number;
  action?: { label: string; onClick: () => void };
}

type ToastSink = (
  title: string,
  options: NotificationToastOptions & {
    duration: number;
    onAutoClose: () => void;
    onDismiss: () => void;
  },
) => void;

export interface ToastQueueDeps {
  /** Test seam — defaults to sonner `toast`. */
  showToast?: ToastSink;
  /** Test seam — defaults to setTimeout/clearTimeout. */
  timers?: {
    setTimeout: typeof setTimeout;
    clearTimeout: typeof clearTimeout;
  };
}

export const MAX_IN_FLIGHT = 3;
export const MIN_GAP_MS = 1000;
// Sonner pauses auto-close timers while hovered, so onAutoClose may fire late
// or never; the fallback frees the slot anyway to guarantee drain progress.
export const FALLBACK_SLACK_MS = 2000;
const DEFAULT_DURATION_MS = 4000;

interface QueuedToast {
  title: string;
  options: NotificationToastOptions;
}

export class NotificationToastQueue {
  private queue: QueuedToast[] = [];
  private inFlight = 0;
  private gapTimer: ReturnType<typeof setTimeout> | null = null;
  private generation = 0;
  private readonly showToast: ToastSink;
  private readonly setT: typeof setTimeout;
  private readonly clearT: typeof clearTimeout;

  constructor(deps: ToastQueueDeps = {}) {
    this.showToast = deps.showToast ?? ((title, options) => void toast(title, options));
    // Bound: browsers throw "Illegal invocation" when window.setTimeout is
    // called with a foreign `this` (here, the queue instance via this.setT()).
    this.setT = deps.timers?.setTimeout ?? setTimeout.bind(globalThis);
    this.clearT = deps.timers?.clearTimeout ?? clearTimeout.bind(globalThis);
  }

  enqueue(title: string, options: NotificationToastOptions = {}): void {
    this.queue.push({ title, options });
    this.drain();
  }

  /** Drop queued toasts and cancel timers (logout). Visible toasts expire on
   *  their own; late sonner callbacks from before the reset are ignored. */
  reset(): void {
    this.queue = [];
    this.inFlight = 0;
    this.generation++;
    if (this.gapTimer) {
      this.clearT(this.gapTimer);
      this.gapTimer = null;
    }
  }

  // Diagnostics — used by tests and dev tooling.
  pendingCount(): number {
    return this.queue.length;
  }
  inFlightCount(): number {
    return this.inFlight;
  }

  private drain(): void {
    if (this.queue.length === 0) return;
    if (this.inFlight >= MAX_IN_FLIGHT) return;
    if (this.gapTimer) return;

    const { title, options } = this.queue.shift()!;
    const duration = options.duration ?? DEFAULT_DURATION_MS;
    this.inFlight++;

    const generation = this.generation;
    let settled = false;
    let fallbackTimer: ReturnType<typeof setTimeout> | null = null;
    const settle = () => {
      if (settled) return;
      settled = true;
      if (fallbackTimer) {
        this.clearT(fallbackTimer);
        fallbackTimer = null;
      }
      if (generation !== this.generation) return;
      this.inFlight--;
      this.drain();
    };

    this.showToast(title, { ...options, duration, onAutoClose: settle, onDismiss: settle });
    fallbackTimer = this.setT(settle, duration + FALLBACK_SLACK_MS);

    // One release per gap window — staggers bursts instead of filling all
    // free slots at once.
    this.gapTimer = this.setT(() => {
      this.gapTimer = null;
      this.drain();
    }, MIN_GAP_MS);
  }
}

export const notificationToastQueue = new NotificationToastQueue();

export function queueNotificationToast(
  title: string,
  options: NotificationToastOptions = {},
): void {
  notificationToastQueue.enqueue(title, options);
}
